// Shared client-side math + labels for the «Сходимость» analytics tab.
//
//   * timeToThreshold — interpolate hours-to-reach a |dR| threshold
//   * drColor / drTextColor — |dR| (mm) → 7-stop HSL gradient (PORT of
//     HeatmapStationConfig.drColor — keep the two in sync)
//   * hoursColor / hoursTextColor — hours-to-threshold → green→red scale
//   * monoColor / monoText — colour-blind-safe parchment→ink ramp
//   * formatLen / formatHours / formatSec / formatObsDate — operator labels
//   * formatConfig — COMPACT convergence config labels (all4/gps/gal/glo/bds);
//     NOTE: distinct from the shared daily `./formatConfig`, whose canonical
//     keys (`beidou,galileo,glonass,gps`, `glonass`, …) differ from the short
//     keys the convergence backend (`_gnss_label_for_systems`) emits.
//
// All values are mm (|dR|) and hours (interval length / time-to-threshold).
// No React here — pure functions, unit-testable.

export type Stop = readonly [value: number, h: number, s: number, l: number];

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function interp(stops: readonly Stop[], v: number): string {
  if (v <= stops[0]![0]) {
    const [, h, s, l] = stops[0]!;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
  const last = stops[stops.length - 1]!;
  if (v >= last[0]) return `hsl(${last[1]}, ${last[2]}%, ${last[3]}%)`;
  for (let i = 1; i < stops.length; i++) {
    const hi = stops[i]!;
    if (v <= hi[0]) {
      const lo = stops[i - 1]!;
      const t = (v - lo[0]) / (hi[0] - lo[0]);
      return `hsl(${Math.round(lerp(lo[1], hi[1], t))}, ${Math.round(
        lerp(lo[2], hi[2], t),
      )}%, ${Math.round(lerp(lo[3], hi[3], t))}%)`;
    }
  }
  return `hsl(${last[1]}, ${last[2]}%, ${last[3]}%)`;
}

function lightnessAt(stops: readonly Stop[], v: number): number {
  if (v <= stops[0]![0]) return stops[0]![3];
  const last = stops[stops.length - 1]!;
  if (v >= last[0]) return last[3];
  for (let i = 1; i < stops.length; i++) {
    const hi = stops[i]!;
    if (v <= hi[0]) {
      const lo = stops[i - 1]!;
      return lerp(lo[3], hi[3], (v - lo[0]) / (hi[0] - lo[0]));
    }
  }
  return last[3];
}

// ── |dR| (mm) — PORT of HeatmapStationConfig._DR_STOPS (monotonic:
//    0 mm = saturated green … 100 mm = deep red). ─────────────────
const DR_STOPS: readonly Stop[] = [
  [0, 140, 60, 38],
  [5, 135, 55, 50],
  [10, 120, 45, 70],
  [15, 75, 70, 60],
  [30, 35, 85, 55],
  [50, 10, 75, 55],
  [100, 0, 75, 45],
];

// ── hours-to-threshold — fast (green) … slow (red). Tuned for the
//    1.5–24 h interval grid; 36 h clamps the «never» tail. ────────
const HOURS_STOPS: readonly Stop[] = [
  [1.5, 140, 60, 38],
  [3, 135, 55, 50],
  [6, 120, 45, 68],
  [10, 75, 70, 58],
  [16, 35, 85, 55],
  [24, 10, 75, 55],
  [36, 0, 75, 45],
];

export const drColor = (mm: number | null): string =>
  mm == null ? 'var(--surface-2)' : interp(DR_STOPS, mm);
export const drTextColor = (mm: number | null): string =>
  mm == null ? 'var(--ink-3)' : lightnessAt(DR_STOPS, mm) < 55 ? '#fff' : 'var(--ink)';

export const hoursColor = (h: number | null): string =>
  h == null ? 'var(--surface-2)' : interp(HOURS_STOPS, h);
export const hoursTextColor = (h: number | null): string =>
  h == null ? 'var(--ink-3)' : lightnessAt(HOURS_STOPS, h) < 55 ? '#fff' : 'var(--ink)';

/** Colour-blind-safe monochrome ramp (light parchment → dark ink) for the
 *  matrix «монохром» mode. `worse` rises with the badness of the value. */
export function monoColor(v: number | null, min: number, max: number): string {
  if (v == null) return 'var(--surface-2)';
  const f = Math.max(0, Math.min(1, (v - min) / (max - min)));
  return `hsl(70, 12%, ${Math.round(92 - f * 64)}%)`;
}
export function monoText(v: number | null, min: number, max: number): string {
  if (v == null) return 'var(--ink-3)';
  const f = Math.max(0, Math.min(1, (v - min) / (max - min)));
  return f > 0.5 ? '#fff' : 'var(--ink)';
}

/**
 * Hours of observation needed for |dR| to fall to `threshold` mm, by linear
 * interpolation in REAL hours between adjacent curve points. `values[i]`
 * aligns to `lengths[i]` (ascending). |dR| decreases as length grows.
 * Returns null when the curve never reaches the threshold within the grid.
 */
export function timeToThreshold(
  values: (number | null)[],
  threshold: number,
  lengths: number[],
): number | null {
  for (let i = 0; i < lengths.length; i++) {
    const v = values[i];
    if (v == null) continue;
    if (v <= threshold) {
      let j = i - 1;
      while (j >= 0 && values[j] == null) j--;
      if (j < 0) return lengths[i]!;
      const v0 = values[j]!;
      const l0 = lengths[j]!;
      const l1 = lengths[i]!;
      if (v0 === v) return l1;
      return l0 + ((v0 - threshold) / (v0 - v)) * (l1 - l0);
    }
  }
  return null;
}

export function median(arr: number[]): number | null {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

export const formatLen = (h: number): string =>
  h % 1 === 0 ? `${h}ч` : `${h.toFixed(1)}ч`;

export const formatHours = (h: number | null): string =>
  h == null ? '—' : `${h.toFixed(1)}ч`;

export const formatSec = (v: number | null): string => {
  if (v == null) return '—';
  const m = Math.floor(v / 60);
  const s = Math.round(v % 60);
  return m === 0 ? `${s}с` : `${m}м${String(s).padStart(2, '0')}с`;
};

export const formatObsDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

/** Default operator threshold presets (mm). Custom value lives in tab state. */
export const THRESHOLD_PRESETS = [5, 10, 20] as const;

// ── compact convergence config labels ──────────────────────────────
// The convergence backend (`analytics.convergence._ui_config_label` →
// `_gnss_label_for_systems`) emits SHORT keys: `all4` / `gps` / `gal` /
// `glo` / `bds` (and rare `+`-joined combos). The shared daily
// `./formatConfig` expects CANONICAL keys instead, so it can't render these.
// This compact labeller matches the prototype's chip text / `cfg-*` colours.
const CONV_CONFIG_LABEL: Record<string, string> = {
  all4: 'все 4',
  gps: 'GPS',
  gal: 'GAL',
  glo: 'ГЛО',
  bds: 'BDS',
};

export function formatConfig(key: string): string {
  return CONV_CONFIG_LABEL[key] ?? key.toUpperCase();
}
